import React, { useState, useEffect, useMemo } from 'react';
import { useDialogNodeContextOptional } from '@/contexts/DialogNodeContext';
import { DialogNode, DialogOption } from '@/lib/stores/dialogStore';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { Label } from '@/components/ui/label';
import { TextAreaElement } from '@/components/wizards/wizard-elements/TextAreaElement';
import { Input } from '@/components/ui/InputDefault';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { Button } from '@/components/ui/button';
import { Save, Plus, Trash2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

interface NodeInfoSidebarProps {
  onNodeUpdate: (nodeId: string, updates: Partial<DialogNode>) => void;
  availableNodes?: Array<{ node_identifier: string; name?: string }>;
  availableDialogs?: Array<{ id: string; name: string }>;
}

export default function StructureSidebarNodeInfo({ 
  onNodeUpdate,
  availableNodes = [],
  availableDialogs = []
}: NodeInfoSidebarProps) {
  const { t } = usePlatformTranslation();
  const dialogNodeContext = useDialogNodeContextOptional();
  
  // State
  const [isEditing, setIsEditing] = useState(false);
  const [editedNode, setEditedNode] = useState<DialogNode | null>(null);

  const selectedNode = dialogNodeContext?.selectedNodeData || null;

  // Load node data when selected node changes
  useEffect(() => {
    if (!selectedNode) {
      setEditedNode(null);
      setIsEditing(false);
      return;
    }

    // Deep clone to avoid reference issues
    setEditedNode(JSON.parse(JSON.stringify(selectedNode)));
  }, [selectedNode]);

  // Handle node field update
  const updateNodeField = (updates: Partial<DialogNode>) => {
    if (!editedNode) return;
    if (!isEditing) setIsEditing(true);

    const updatedNode = { ...editedNode, ...updates };
    setEditedNode(updatedNode);
  };

  // Handle option update
  const updateOption = (optionIndex: number, updates: Partial<DialogOption>) => {
    if (!editedNode || !editedNode.options) return;
    if (!isEditing) setIsEditing(true);

    const newOptions = [...editedNode.options];
    newOptions[optionIndex] = { ...newOptions[optionIndex], ...updates };

    const updatedNode = { ...editedNode, options: newOptions };
    setEditedNode(updatedNode);
  };

  // Add new option
  const addOption = () => {
    if (!editedNode) return;
    if (!isEditing) setIsEditing(true);

    const existingOptions = editedNode.options || [];
    const newOption: DialogOption = {
      id: `option_${Date.now()}_${Math.random()}`,
      node_id: editedNode.id,
      option_text: '',
      translatable: true,
      next_node: null,
      next_dialog: null,
      next_dialog_node: null,
      order_index: existingOptions.length
    };

    const updatedNode = { ...editedNode, options: [...existingOptions, newOption] };
    setEditedNode(updatedNode);
  };

  // Remove option
  const removeOption = (optionIndex: number) => {
    if (!editedNode || !editedNode.options) return;
    if (!isEditing) setIsEditing(true);

    const newOptions = editedNode.options.filter((_, idx) => idx !== optionIndex);
    // Reorder
    const reorderedOptions = newOptions.map((opt, idx) => ({ ...opt, order_index: idx }));

    const updatedNode = { ...editedNode, options: reorderedOptions };
    setEditedNode(updatedNode);
  };

  // Handle save
  const handleSave = () => {
    if (!editedNode || !selectedNode) return;

    // Call the update handler
    onNodeUpdate(selectedNode.id, editedNode);

    // Reset editing state
    setIsEditing(false);
  };

  // Handle cancel
  const handleCancel = () => {
    setEditedNode(selectedNode ? JSON.parse(JSON.stringify(selectedNode)) : null);
    setIsEditing(false);
  };

  // Show empty state if no node selected
  if (!selectedNode || !editedNode) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p>{t('platform.layout.structure-menu.select_a_node_to_view_details')}</p>
        <p className="text-xs mt-2">Click on a node in the flow to edit its properties</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Node Details
            </h3>
          </div>

          {/* Node Name */}
          <div>
            <Label className="text-sm font-medium">Node Name</Label>
            <Input
              value={editedNode.name || ''}
              onChange={(e) => {
                if (!isEditing) setIsEditing(true);
                updateNodeField({ name: e.target.value });
              }}
              className="mt-1"
              placeholder="Node name"
            />
          </div>

          {/* Node Identifier (Read-only) */}
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Node Identifier</Label>
            <Input
              value={editedNode.node_identifier || ''}
              className="mt-1 bg-muted"
              disabled
            />
            <p className="text-xs text-muted-foreground mt-1">
              Auto-generated from node name
            </p>
          </div>

          {/* Node Type */}
          <div>
            <Label className="text-sm font-medium">Node Type</Label>
            <Select
              value={editedNode.type}
              onValueChange={(value) => updateNodeField({ type: value as any })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dialog">Dialog</SelectItem>
                <SelectItem value="context">Context</SelectItem>
                <SelectItem value="question">Question</SelectItem>
                <SelectItem value="condition">Condition</SelectItem>
                <SelectItem value="setter">Setter</SelectItem>
                <SelectItem value="end-dialog">End-Dialog</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Text Content */}
          {editedNode.type !== 'setter' && (
            <div>
              <Label className="text-sm font-medium">Text</Label>
              <TextAreaElement
                value={editedNode.text || ''}
                onChange={(value) => updateNodeField({ text: value })}
                placeholder="Node text"
                rows={4}
                className="mt-1"
              />
            </div>
          )}

          {/* Translatable */}
          {editedNode.type !== 'setter' && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="translatable"
                checked={editedNode.translatable}
                onCheckedChange={(checked) => 
                  updateNodeField({ translatable: !!checked })
                }
              />
              <Label htmlFor="translatable" className="text-sm">
                Translatable
              </Label>
            </div>
          )}

          {/* Speaker */}
          {(editedNode.type === 'dialog' || editedNode.type === 'context') && (
            <div>
              <Label className="text-sm font-medium">Speaker</Label>
              <Input
                value={editedNode.speaker || ''}
                onChange={(e) => updateNodeField({ speaker: e.target.value })}
                className="mt-1"
                placeholder="Speaker name"
              />
            </div>
          )}

          {/* Next Node (for dialog/context/end-dialog fields) */}
          {(editedNode.type === 'dialog' || editedNode.type === 'context' || editedNode.type === 'end-dialog') && (
            <div>
              <Label className="text-sm font-medium">Next Node</Label>
              <Select
                value={editedNode.next_node || '_none'}
                onValueChange={(value) => 
                  updateNodeField({ next_node: value === '_none' ? null : value })
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select node" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">None</SelectItem>
                  {availableNodes.map(node => (
                    <SelectItem key={node.node_identifier} value={node.node_identifier}>
                      {node.name || node.node_identifier}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Options (for question type) */}
          {editedNode.type === 'question' && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-medium">Options</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addOption}
                  className="h-7 text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Option
                </Button>
              </div>

              {editedNode.options && editedNode.options.length > 0 ? (
                <div className="space-y-3">
                  {editedNode.options.map((option, optIndex) => (
                    <div key={option.id || optIndex} className="border rounded-lg p-3 bg-card">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-xs font-medium">Option {optIndex + 1}</Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeOption(optIndex)}
                          className="h-6 w-6 p-0 text-destructive"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>

                      <div className="space-y-2">
                        {/* Option Text */}
                        <div>
                          <Label className="text-xs">Text</Label>
                          <Input
                            value={option.option_text || ''}
                            onChange={(e) => 
                              updateOption(optIndex, { option_text: e.target.value })
                            }
                            className="mt-1 h-8 text-xs"
                            placeholder="Option text"
                          />
                        </div>
                          {/* Translatable */}
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id={`opt-translatable-${option.id}`}
                              checked={option.translatable}
                              onCheckedChange={(checked) => 
                                updateOption(optIndex, { translatable: !!checked })
                              }
                            />
                            <Label htmlFor={`opt-translatable-${option.id}`} className="text-xs">
                              Translatable
                            </Label>
                          </div>

                          {/* Next Node */}
                          <div>
                            <Label className="text-xs">Next Node</Label>
                            <Select
                              value={option.next_node || '_none'}
                              onValueChange={(value) => 
                                updateOption(optIndex, { 
                                  next_node: value === '_none' ? null : value 
                                })
                              }
                            >
                              <SelectTrigger className="mt-1 h-7 text-xs">
                                <SelectValue placeholder="Select node" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_none">None</SelectItem>
                                {availableNodes.map(node => (
                                  <SelectItem key={node.node_identifier} value={node.node_identifier}>
                                    {node.name || node.node_identifier}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Next Dialog */}
                          <div>
                            <Label className="text-xs">Jump to Dialog</Label>
                            <Select
                              value={option.next_dialog || '_none'}
                              onValueChange={(value) => 
                                updateOption(optIndex, { 
                                  next_dialog: value === '_none' ? null : value,
                                  next_dialog_node: value === '_none' ? null : option.next_dialog_node
                                })
                              }
                            >
                              <SelectTrigger className="mt-1 h-7 text-xs">
                                <SelectValue placeholder="Select dialog" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_none">None</SelectItem>
                                {availableDialogs.map(dialog => (
                                  <SelectItem key={dialog.id} value={dialog.id}>
                                    {dialog.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground text-center py-4 border rounded">
                  No options yet. Click "Add Option" to create one.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Save/Cancel buttons */}
      {isEditing && (
        <div className="p-3 border-t border-border bg-muted flex gap-2">
          <Button
            onClick={handleSave}
            className="flex-1"
            variant="default"
            size="sm"
          >
            <Save className="h-4 w-4 mr-2" />
            Save
          </Button>
          <Button
            onClick={handleCancel}
            variant="outline"
            size="sm"
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
